// 'use client' is load-bearing, not decoration: this module calls React hooks,
// and it is reachable from the `@auctogon/ui/web` barrel. A Server Component
// importing ANY symbol from that barrel pulls this file into the RSC graph,
// where hooks don't exist. Guarded by web-barrel-client-directive.test.ts
// (BL-1124).
'use client';

/**
 * useTopmostEscape — close ONLY the topmost overlay when Escape is pressed.
 *
 * The one implementation of overlay Escape handling for the whole design
 * system, per [ADR 030](../../../../docs/adrs/030_DESIGN_SYSTEM_FOUNDATION.md)
 * Decision 2: `packages/ui` core owns behaviour (keyboard handling, ARIA, focus
 * management) while each app's skin binds appearance only. Same consolidation
 * shape as its sibling `useFocusTrap`, which BUG-1386 extracted from
 * `components/Modal.tsx` for the same reason.
 *
 * ## Why a registry and not just a listener (BUG-1399)
 *
 * Every overlay used to register its own bare `document` keydown listener with
 * no notion of the others. Listeners on the same target all fire for one event,
 * and none of them called `stopPropagation` (they couldn't usefully — they're
 * peers on `document`, not ancestor and descendant). So one Escape closed the
 * WHOLE STACK: open the photo lightbox from inside the lot editor, press
 * Escape, and the editor closed with it — the operator lost their place instead
 * of dismissing the thing they just opened.
 *
 * Stacking order can't be read off the DOM either: every overlay portals to
 * `document.body`, so a nested overlay is a *sibling* of its host, not a
 * descendant. `z-index` doesn't settle it either — an overlay may pin an
 * explicit layer (`ImageLightbox` does), so a higher number doesn't prove
 * "opened later". What DOES hold is mount order: React commits a nested
 * overlay's effects after its host's, because the nested one mounts while the
 * host is already open. So the registry records open overlays in mount order
 * and the last one in is the topmost.
 *
 * ## The contract
 *
 * - Only the overlay that is **last to register** reacts to Escape. Everything
 *   below it ignores the key entirely.
 * - Registration is scoped to `active`. An overlay that unmounts, closes, or
 *   opts out of Escape (`closeOnEscape={false}`) leaves the stack, which
 *   promotes whatever is beneath it — so dismissing a nested overlay hands
 *   Escape back to its host for the next press.
 * - Order is mount order, NOT z-index. A host that renders its nested overlay
 *   while already open always registers first.
 *
 * @param active - Whether this overlay should participate. Pass
 *   `isOpen && closeOnEscape` — a closed overlay must not hold a slot, or it
 *   would swallow Escape for the one actually on screen.
 * @param onEscape - Called with the keydown event when Escape is pressed AND
 *   this overlay is topmost. The event lets a caller defer to a handler that
 *   already claimed the key (`defaultPrevented`) or to a field being typed in.
 * @returns `isTopmost()` — whether this overlay is currently the top of the
 *   stack. Call it from an event handler, not during render (BL-2087).
 */

import { useCallback, useEffect, useRef } from 'react';

/**
 * Overlays currently accepting Escape, in mount order — the last entry is the
 * topmost. Module-level so every overlay in the app shares one stack; each
 * entry is an opaque per-instance token, never a component or DOM reference,
 * so nothing here can retain a detached tree.
 */
const escapeStack: symbol[] = [];

/** Test-only: read the current depth. Never branch on this in product code. */
export function getOverlayEscapeStackDepth(): number {
  return escapeStack.length;
}

export function useTopmostEscape(active: boolean, onEscape: (event: KeyboardEvent) => void): () => boolean {
  const idRef = useRef<symbol | null>(null);
  idRef.current ??= Symbol('overlay-escape');
  const id = idRef.current;

  // Kept in a ref so a caller passing an inline arrow (most of them do) doesn't
  // tear down and re-add the listener every render.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return;

    // Defensive removal before push: React 18 StrictMode invokes an effect,
    // its cleanup, then the effect again. Without this a double-invoke could
    // leave two entries for one overlay, and the stale one would keep the real
    // topmost from ever matching.
    const drop = () => {
      const at = escapeStack.lastIndexOf(id);
      if (at !== -1) escapeStack.splice(at, 1);
    };
    drop();
    escapeStack.push(id);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Peers on `document` all receive this event; only the top acts on it.
      if (escapeStack[escapeStack.length - 1] !== id) return;
      onEscapeRef.current(event);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      drop();
    };
  }, [active, id]);

  // BL-2087: the same stack answers "is this overlay on top?" for pointer
  // dismissal. A non-modal panel must not close on a click that landed on a
  // nested overlay's backdrop — that click belongs to the overlay above it.
  // Read at event time, never during render: the stack is not React state.
  return useCallback(() => escapeStack[escapeStack.length - 1] === id, [id]);
}
